import type { LensAggregate, LensCardType, LensColumnKind } from '~/types/leLens';

/**
 * The panel's four tabs, one to one with legacy LensCardDialog.tsx's Chart,
 * Data, Card details and Format & style (in that legacy order: Chart=0,
 * Data=1, Details=2, Style=3). Data has real content here; Chart, Details
 * and Style bridge to the legacy dialog at the matching tab until their
 * content is lifted into the panel.
 */
export const LENS_PANEL_TABS = ['data', 'chart', 'details', 'style'] as const;
export type LensPanelTab = (typeof LENS_PANEL_TABS)[number];

export const LENS_PANEL_TAB_LABELS: Record<LensPanelTab, string> = {
  data: 'Data',
  chart: 'Chart',
  details: 'Details',
  style: 'Style',
};

/** The legacy LensCardDialog tab index each bridged panel tab opens. */
export const LEGACY_TAB_FOR_PANEL_TAB: Record<Exclude<LensPanelTab, 'data'>, 0 | 2 | 3> = {
  chart: 0,
  details: 2,
  style: 3,
};

/**
 * The inverse: which panel tab a legacy tab index opens. Used at the mount
 * point, where a card's own menu still hands out a legacy
 * `LensCardEditTab` (0-3) from `cards/LensCardShell.tsx`.
 */
export const PANEL_TAB_FOR_LEGACY_TAB: Record<0 | 1 | 2 | 3, LensPanelTab> = {
  0: 'chart',
  1: 'data',
  2: 'details',
  3: 'style',
};

/**
 * A shelf a card's view can be assigned to. 'values' is the many-valued
 * Columns shelf a table card fills, in place of the single-valued x/series/value
 * shelves every other card type uses.
 */
export const SHELF_KEYS = ['x', 'series', 'value', 'values'] as const;
export type ShelfKey = (typeof SHELF_KEYS)[number];

/** Kept from today's Data tab so nothing relearns, per 02_RIGHT_PANEL_SPEC.md. */
export const SHELF_LABELS: Record<ShelfKey, string> = {
  x: 'Group by',
  series: 'Split by',
  value: 'Measure',
  values: 'Columns',
};

/** Which shelves a card type offers, in the order the 2x2 grid draws them. */
export function shelvesForCardType(type: LensCardType): Array<ShelfKey> {
  if (type === 'metric') {
    return ['value'];
  }
  if (type === 'table') {
    return ['x', 'series', 'values'];
  }
  return ['x', 'series', 'value'];
}

export const PANEL_FIELD_GROUP_ORDER = ['Dates', 'Dimensions', 'Measures', 'Calculated'] as const;
export type PanelFieldGroup = (typeof PANEL_FIELD_GROUP_ORDER)[number];

export const PANEL_FIELD_GROUP_BY_KIND: Record<LensColumnKind, PanelFieldGroup> = {
  date: 'Dates',
  dimension: 'Dimensions',
  measure: 'Measures',
  calculated: 'Calculated',
};

/** One row the field list shows, derived from a dataset's declared columns. */
export type PanelField = {
  name: string;
  type: string;
  kind: LensColumnKind;
  label?: string;
  /** Calculated columns only: the formula shown on hover. */
  formula?: string;
  /** Plain-English meaning, from dbt docs on a governed dataset. Shown as a tooltip. */
  description?: string;
  /** A governed measure's declared combine rule: the Combine control's default, and for
   *  countDistinct, its only honest choice. */
  aggregate?: LensAggregate;
  /** A governed ratio measure: dropping it onto the Measure shelf sets view.ratio directly
   *  rather than view.value/aggregate. */
  ratio?: { of: string; per: string };
};

/** Rows already loaded, shaped the way the canvas already shapes them. */
export type PanelShaped = { rows: Array<Record<string, unknown>>; columns: Array<string> };

/** What field-hover highlights on the canvas: the field, and which cards use it. */
export type PanelFieldHover = { datasetId: string; column: string; cardIds: Array<string> } | null;

export const AGGREGATE_OPTIONS: Array<{ value: LensAggregate; label: string }> = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'count', label: 'Count rows' },
  { value: 'countDistinct', label: 'Count distinct' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'first', label: 'First value' },
];

// ---------------------------------------------------------------------------
// The combined pane (Edit / Ask), replacing the four tabs. Shared contract for
// the shell, the section contents and the Ask pane; owned by the coordinator.
// ---------------------------------------------------------------------------

/** The pane moves between the conversation, the selected card and Lens-wide setup. */
export type LensPanelMode = 'edit' | 'ask' | 'lens';

/** Edit mode's four collapsible sections, top to bottom. */
export const LENS_PANEL_SECTIONS = ['visual', 'layout', 'data', 'format'] as const;
export type LensPanelSection = (typeof LENS_PANEL_SECTIONS)[number];

export const LENS_PANEL_SECTION_LABELS: Record<LensPanelSection, string> = {
  visual: 'Chart',
  layout: 'Layout',
  data: 'Data',
  format: 'Formatting',
};

/** Which section each former tab's content lives in, so the card menu and URL state keep working. */
export const PANEL_SECTION_FOR_TAB: Record<LensPanelTab, LensPanelSection> = {
  chart: 'visual',
  details: 'layout',
  data: 'data',
  style: 'format',
};

/** Pane width by breakpoint band; below `lg` the pane is a full-width sheet. */
export const LENS_PANEL_WIDTH = { xl: 560, lg: 480 } as const;

/**
 * What the shell needs to render Ask mode inside the pane. The page owns the
 * draft: applying a proposal writes the proposed card into the same draft a
 * manual edit uses (live preview on the canvas), and the inline Save persists
 * it through the same path as the Save bar.
 */
export type LensPanelAskRenderArgs = {
  card?: import('~/types/leLens').LensCard;
  /** Writes the proposed card (type, datasetId, view, config, title) into the pane's draft. */
  onApplyProposal: (proposed: import('~/types/leLens').LensCard) => void;
  /** Persists the current draft to the Lens. */
  onSaveDraft: () => Promise<void>;
  /** True once a proposal (or any edit) has changed the draft and not been saved. */
  dirty: boolean;
  isSaving: boolean;
  /** The Lens this chat belongs to, for the pane's scope header. */
  lensTitle?: string;
  /** The person may change this Lens at all. Viewers never save. */
  canEdit: boolean;
  /** Drops the selected card so the chat is about the whole Lens again. */
  onClearCard: () => void;
};

/** Reported by the page whenever the pane opens, closes or changes width, so the shell can keep the board composer clear of it. */
export type LensEditorPanelState = { open: boolean; width: number };
